import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatDate } from "@/lib/api";
import { PriceDisplay } from "@/components/PriceDisplay";
import { useAuth } from "@/contexts/AuthContext";
import { LockKey, Clock, PlayCircle, Images, X, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function getImages(item) {
  if (Array.isArray(item.images) && item.images.length > 0) return item.images;
  if (item.image_url) return [item.image_url];
  return [];
}

function youtubeEmbedUrl(url) {
  if (!url) return null;
  if (url.includes("/embed/")) return url;
  // Handle youtu.be/<id>
  let id = null;
  const youtuBeMatch = url.match(/youtu\.be\/([\w-]+)/);
  if (youtuBeMatch) id = youtuBeMatch[1];
  // Handle youtube.com/watch?v=<id>
  const watchMatch = url.match(/[?&]v=([\w-]+)/);
  if (!id && watchMatch) id = watchMatch[1];
  return id ? `https://www.youtube.com/embed/${id}` : null;
}

function InventoryDetailModal({ item, onClose }) {
  const images = getImages(item);
  const [activeIdx, setActiveIdx] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const embed = youtubeEmbedUrl(item.youtube_url);

  const next = () => setActiveIdx((i) => (i + 1) % Math.max(images.length, 1));
  const prev = () => setActiveIdx((i) => (i - 1 + Math.max(images.length, 1)) % Math.max(images.length, 1));

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto" onClick={onClose} data-testid="inventory-detail-modal">
      <div className="max-w-5xl mx-auto my-8 px-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase">{item.category} · {item.condition}</div>
          <button onClick={onClose} className="btn-secondary text-xs py-2 px-3" data-testid="inventory-detail-close">
            <X size={14} weight="bold" /> CLOSE
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            {showVideo && embed ? (
              <div className="aspect-video bg-black border border-[#262626] relative">
                <iframe src={embed} className="w-full h-full" title={item.title} allowFullScreen />
                <button onClick={() => setShowVideo(false)} className="absolute top-2 right-2 bg-[#0A0A0A] border border-[#262626] text-white p-2" data-testid="close-video-overlay">
                  <X size={14} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="aspect-video bg-[#0A0A0A] border border-[#262626] overflow-hidden relative">
                {images.length > 0 ? (
                  <img src={images[activeIdx]} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-mono text-xs text-[#52525B]">NO IMAGE</div>
                )}
                {images.length > 1 && (
                  <>
                    <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-[#0A0A0A]/80 border border-[#262626] text-white p-2 hover:border-[#00D4FF]" data-testid="gallery-prev">
                      <ArrowLeft size={14} weight="bold" />
                    </button>
                    <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#0A0A0A]/80 border border-[#262626] text-white p-2 hover:border-[#00D4FF]" data-testid="gallery-next">
                      <ArrowRight size={14} weight="bold" />
                    </button>
                    <div className="absolute bottom-2 right-2 bg-[#0A0A0A]/80 border border-[#262626] font-mono text-[10px] text-white tracking-widest px-2 py-1">
                      {activeIdx + 1} / {images.length}
                    </div>
                  </>
                )}
                {embed && (
                  <button onClick={() => setShowVideo(true)} className="absolute top-2 left-2 bg-[#00D4FF] text-black px-3 py-1.5 font-chivo font-bold text-xs uppercase tracking-wider hover:bg-[#33DDFF]" data-testid="play-inventory-video">
                    <PlayCircle size={14} weight="fill" /> PLAY VIDEO
                  </button>
                )}
              </div>
            )}

            {images.length > 1 && !showVideo && (
              <div className="grid grid-cols-5 sm:grid-cols-6 gap-2 mt-2">
                {images.map((url, idx) => (
                  <button key={url + idx} onClick={() => setActiveIdx(idx)} className={`aspect-square overflow-hidden border ${idx === activeIdx ? "border-[#00D4FF]" : "border-[#262626] hover:border-[#404040]"}`} data-testid={`gallery-thumb-${idx}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5 surface p-6">
            <h1 className="font-chivo font-black text-2xl text-white uppercase tracking-tight mb-3">{item.title}</h1>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="tag">{item.condition}</span>
              <span className="tag">HRS {item.hours || "n/a"}</span>
              {item.member_only && <span className="tag tag-active"><LockKey size={10} weight="fill" /> MEMBER EARLY ACCESS</span>}
            </div>
            <p className="font-plex text-sm text-[#9CA3AF] leading-relaxed mb-6 whitespace-pre-wrap">{item.description}</p>
            {item.member_only && (
              <div className="font-mono text-xs text-[#00D4FF] mb-4">Public release: {formatDate(item.released_at)}</div>
            )}
            <div className="border-t border-[#262626] pt-4 flex items-end justify-between gap-4">
              <Link to="/support" className="btn-secondary text-xs" data-testid={`detail-inquire-${item.id}`}>INQUIRE</Link>
              <PriceDisplay msrp={item.msrp_price} member={item.member_price} showMember={item.show_member_price} testId={`detail-${item.id}-price`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Inventory() {
  const { isMember } = useAuth();
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/inventory");
        setItems(data);
      } catch (err) {
        console.error("Failed to load inventory:", err);
      }
      setLoading(false);
    })();
  }, []);

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category)))];
  const filtered = activeCat === "All" ? items : items.filter((i) => i.category === activeCat);

  return (
    <Layout>
      {selected && <InventoryDetailModal item={selected} onClose={() => setSelected(null)} />}
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8 items-end">
          <div className="lg:col-span-8">
            <div className="section-label mb-3">— EARLY ACCESS INVENTORY</div>
            <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="inventory-headline">Used · Demo · Refurbished</h1>
            <p className="font-plex text-[#9CA3AF] mt-3 max-w-2xl">
              Members get a 5-day head start on every used machine, demo unit, refurbished item, and trade-in we list.
            </p>
          </div>
          {!isMember && (
            <div className="lg:col-span-4 surface p-4 flex items-center justify-between gap-4">
              <div>
                <div className="font-mono text-xs text-[#00D4FF] tracking-widest uppercase">JUMP THE LINE</div>
                <div className="font-plex text-xs text-[#9CA3AF] mt-1">See member-only listings 5 days early</div>
              </div>
              <Link to="/register" className="btn-primary text-xs py-2 px-4" data-testid="inventory-join-cta">Join</Link>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c) => (
            <button key={c} onClick={() => setActiveCat(c)} className={`tag ${activeCat === c ? "tag-active" : ""}`} data-testid={`inventory-category-${c.toLowerCase()}`}>
              {c}
            </button>
          ))}
        </div>

        {(() => {
          if (loading) return <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>;
          if (filtered.length === 0) return <div className="surface p-12 text-center font-mono text-sm text-[#52525B]">No inventory currently listed.</div>;
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]" data-testid="inventory-grid">
              {filtered.map((item) => {
                const itemImages = getImages(item);
                const primary = itemImages[0];
                const hasVideo = !!youtubeEmbedUrl(item.youtube_url);
                return (
                  <button key={item.id} onClick={() => setSelected(item)} className="bg-[#0A0A0A] hover:bg-[#121212] transition-colors text-left" data-testid={`inventory-card-${item.id}`}>
                    <div className="aspect-video bg-[#0A0A0A] overflow-hidden border-b border-[#262626] relative">
                      {primary && <img src={primary} alt={item.title} className="w-full h-full object-cover" />}
                      {item.member_only && (
                        <div className="absolute top-3 left-3 tag tag-active flex items-center gap-1">
                          <LockKey size={10} weight="fill" /> MEMBER EARLY ACCESS
                        </div>
                      )}
                      <div className="absolute bottom-3 right-3 flex gap-1">
                        {itemImages.length > 1 && (
                          <span className="tag bg-[#0A0A0A]/90"><Images size={10} weight="bold" /> {itemImages.length}</span>
                        )}
                        {hasVideo && (
                          <span className="tag bg-[#0A0A0A]/90 tag-active"><PlayCircle size={10} weight="fill" /> VIDEO</span>
                        )}
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                          <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase">{item.category}</div>
                          <h3 className="font-chivo font-bold text-lg text-white mt-1">{item.title}</h3>
                        </div>
                        <span className="tag">{item.condition}</span>
                      </div>
                      <p className="font-plex text-sm text-[#9CA3AF] line-clamp-2 mb-4">{item.description}</p>
                      <div className="font-mono text-xs text-[#52525B] mb-3 flex items-center gap-2">
                        <Clock size={12} weight="bold" /> Hours: {item.hours || "n/a"}
                        {item.member_only && (
                          <span className="text-[#00D4FF]"> · Public on {formatDate(item.released_at)}</span>
                        )}
                      </div>
                      <div className="flex items-end justify-between border-t border-[#262626] pt-4">
                        <span className="btn-secondary text-xs py-2 px-3 pointer-events-none">VIEW DETAILS</span>
                        <PriceDisplay msrp={item.msrp_price} member={item.member_price} showMember={item.show_member_price} testId={`inventory-${item.id}-price`} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })()}
      </div>
    </Layout>
  );
}
